/* eslint-disable no-restricted-imports -- file uses shared helpers from auth-fe to parse data loader responses */
import {
  Authenticated,
  DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  LoginCredentials,
  LoginResult,
  useAuth as useSharedAuth,
} from '@opendoor/auth-fe';

import { DOMAIN } from 'shared/helpers/globals';

import { SystemError, UnauthenticatedError } from './Error';

/**
 * Helper to return the user regardless of auth state
 * @module platform/auth-fe
 * @returns {user | undefined} - The authenticated user
 * @throws {Redirect} - Redirect to the login page if the user is not authenticated
 */
export const useAuth = () => {
  const { authentication, ...rest } = useSharedAuth();
  return { ...authentication, ...rest };
};

/**
 * Helper to return the authenticated user for auth-gatd routes
 * @module platform/auth-fe
 * @throws {SystemError} - If the user is not authenticated
 */
export const useAuthAuthenticated = (triggeredQuery?: string) => {
  const data = useAuth();
  if (data.state !== 'authenticated' || data.user === null || !data.user) {
    throw new UnauthenticatedError(triggeredQuery);
  }

  // Can be typecast because of above checks
  const auth = { user: data.user, state: data.state } as Authenticated;
  return { ...data, ...auth };
};

export async function verifyLogin(credentials: LoginCredentials): Promise<LoginResult> {
  let payload: unknown;
  if ('googleIdToken' in credentials) {
    payload = { google_id_token: credentials.googleIdToken };
  } else if ('facebookIdToken' in credentials) {
    payload = { facebook_id_token: credentials.facebookIdToken };
  } else {
    payload = {
      human: {
        email: credentials.email,
        password: credentials.password,
      },
    };
  }

  // See `verify_login` at
  // https://github.com/opendoor-labs/web/blob/master/app/controllers/humans/sessions_controller.rb
  let resp;
  try {
    resp = await fetch(`${DOMAIN}/verify_login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    // trackError('login', e as Error);
    return { success: false, displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE };
  }

  if (resp.ok) {
    return { success: true };
  }
  let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;
  try {
    const json = await resp.json();
    if ('error' in json) {
      displayErrorMessage = json['error'];
    }
  } catch (e) {
    if (e instanceof Error) {
      throw e;
    }
    throw new SystemError('login error without error message');
  }
  return {
    success: false,
    displayErrorMessage,
  };
}
